import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useOrderDetails } from './hooks/use-order-details.hook';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { OrderDetailsActionsObserver } from './order-details-actions-observer';
import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { TimelineCard } from '../../../ui/components/pure/timeline/timeline-card/timeline-card.component';
import { useMemo } from 'react';
import { makeCurrentAction, makeTimelineEvent } from './logic/timeline/order-timeline.map';
import { makeOrderDetailsCardProps, makeOrderDetailsHeaderProps } from './logic/order-details-props.map';
import { WorkflowDetailsHeader } from '../../../ui/components/pure/workflow-details-header/workflow-details-header.component';
import { WorkflowCard } from '../../../ui/components/pure/workflow-card/workflow-card.component';
import { TimelineEventProps } from '../../../ui/components/pure/timeline/timeline.types';
import { useLocation } from 'react-router-dom';
import { LocationState } from '../../../ui/components/pure/list-table/list-table.types';
import { useBookingFlowType } from '../list/actions/book-order/use-booking-flow-type.hook';

export const OrderDetailsPage = ({ params }: { params: ProjectPathParams<Paths.order> }) => {
  const { order, timeline, state, handlers } = useOrderDetails(params.projectID, params.orderID);
  const bookingFlowType = useBookingFlowType(order.data?.retailer?.id || params.projectID);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation<LocationState>();

  const timelineEvents = useMemo(() => {
    const events = (timeline.data?.events || []).map((tlEvent) => makeTimelineEvent(tlEvent, bookingFlowType));
    const currentAction = makeCurrentAction(
      timeline.data?.currentAction,
      handlers.setAction,
      order.data,
      bookingFlowType
    );
    return [currentAction, ...events].filter((e): e is TimelineEventProps => !!e);
  }, [timeline.data?.events, timeline.data?.currentAction, handlers.setAction, order.data, bookingFlowType]);

  if (order.isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (order.error?.statusCode === 401) {
    return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
  }

  if (order.error?.statusCode === 403) {
    return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
  }

  if (order.error?.statusCode === 404) {
    return <ErrorFullPage preset="NOT_FOUND" entity="order" />;
  }

  if (order.error || !order.data) {
    return <ErrorFullPage />;
  }

  const headerProps = makeOrderDetailsHeaderProps(params.projectID, order.data, handlers.setAction, bookingFlowType);
  const cardProps = makeOrderDetailsCardProps(order.data);

  return (
    <Box style={{ marginTop: isLargeScreen ? '42px' : '10px' }}>
      <WorkflowDetailsHeader {...headerProps} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column'
        }}
      >
        <Box
          sx={{
            width: isLargeScreen ? '35%' : '100%',
            paddingRight: isLargeScreen ? '3rem' : '0rem',
            flexShrink: 0
          }}
        >
          <WorkflowCard {...cardProps} />
        </Box>
        <Box style={{ flexGrow: 1 }}>
          <TimelineCard
            isError={timeline.isError}
            isLoading={timeline.isLoading}
            title="Latest events"
            events={timelineEvents}
          />
        </Box>

        <OrderDetailsActionsObserver
          order={order.data}
          state={state}
          handlers={handlers}
          retailerID={params.projectID}
          locationState={location.state}
        />
      </Box>
    </Box>
  );
};
